import { createContext, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { localStorageKeys } from "../config/localStorageKeys";
import { useToast } from "../../view/components/ui/use-toast";
import { usersService } from "../services/usersService";
import { CurrentUserResponse } from "../services/usersService/user";
import { AccessLoadingScreen } from "../../view/components/AccessLoadingScreen";
import * as Sentry from "@sentry/react";

const LOADING_EXEMPT_ROUTES = ["/terms", "/policy"];

interface AuthContextValue {
  signedIn: boolean;
  user: CurrentUserResponse | undefined;
  signin(accessToken: string): void;
  signout(): void;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isManager: boolean;
  isLoading: boolean;
  isSupervisor: boolean;
  isLeader: boolean;
  isSeller: boolean;
  isSupport: boolean;
  PERMISSIONS: {
    sellers: {
      approve: boolean;
      block: boolean;
      unicoApprove: boolean;
    };
    team: {
      create: boolean;
      edit: boolean;
      manageGoals: boolean;
      addMembers: boolean;
      removeMembers: boolean;
      addLeader: boolean;
      removeLeader: boolean;
      addSupervisor: boolean;
      addManager: boolean;
      removeManager: boolean;
    };
    tables: {
      create: boolean;
      edit: boolean;
      remove: boolean;
      sort: boolean;
      enableDisable: boolean;
    };
    banners: {
      createStart: boolean;
      create: boolean;
      edit: boolean;
      remove: boolean;
      sort: boolean;
    };
    configs: {
      withdrawalTax: boolean;
      minWithdrawal: boolean;
      dailyWithdrawal: boolean;
      unico: boolean;
      goals: boolean;
    };
    raffles: {
      createStart: boolean;
      create: boolean;
      edit: boolean;
      execute: boolean;
    };
  };
}

export const AuthContext = createContext({} as AuthContextValue);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [signedIn, setSignedIn] = useState<boolean>(() => {
    const storedAccessToken = localStorage.getItem(
      localStorageKeys.ACCESS_TOKEN,
    );

    return !!storedAccessToken;
  });
  const { toast } = useToast();

  const storedAccessToken = localStorage.getItem(localStorageKeys.ACCESS_TOKEN);

  const { isError, data, isLoading } = useQuery({
    queryKey: ["users", "currentUser", storedAccessToken],
    queryFn: async () => usersService.currentUser(),
    enabled: signedIn,
    retry(failureCount) {
      return failureCount < 3;
    },
  });

  const [accessReady, setAccessReady] = useState(false);

  const isLoadingExemptRoute = LOADING_EXEMPT_ROUTES.some((route) =>
    window.location.pathname.startsWith(route)
  );

  const signin = useCallback((accessToken: string) => {
    localStorage.setItem(localStorageKeys.ACCESS_TOKEN, accessToken);

    setAccessReady(false);
    setSignedIn(true);
  }, []);

  const signout = useCallback(async () => {
    localStorage.removeItem(localStorageKeys.ACCESS_TOKEN);
    setAccessReady(false);
    setSignedIn(false);
  }, []);

  useEffect(() => {
    if (data) {
      Sentry.setUser({
        id: data._id,
        username: data.name,
        email: data.email,
      });
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: "Sua sessão expirou!",
      });
      signout();
    }
  }, [isError, signout, toast]);

  const isSuperAdmin = data?.role === "SUPER_ADMIN";
  const isAdmin = data?.role === "ADMIN" || isSuperAdmin;
  const isManager = data?.role === "MANAGER";
  const isSupervisor = data?.role === "SUPERVISOR";
  const isLeader = data?.role === "LEADER";
  const isSeller = data?.role === "USER";
  const isSupport = data?.secondRole === "SUPPORT";

  const PERMISSIONS = {
    sellers: {
      approve: isSupport || isSuperAdmin,
      block: isSupport || isSuperAdmin,
      unicoApprove: isSupport || isSuperAdmin,
    },
    team: {
      create: isSuperAdmin || isAdmin || isSupport,
      edit: isSuperAdmin || isAdmin || isSupport,
      manageGoals: isSuperAdmin || isAdmin || isSupport,
      addMembers:
        isSuperAdmin ||
        isAdmin ||
        isSupport ||
        isManager ||
        isSupervisor ||
        isLeader,
      removeMembers: isSuperAdmin || isAdmin || isSupport,
      addLeader: isSuperAdmin || isAdmin || isSupport,
      removeLeader: isSuperAdmin || isAdmin || isSupport,
      addSupervisor: isSuperAdmin || isAdmin || isSupport,
      addManager: isSuperAdmin || isAdmin || isSupport,
      removeManager: isSuperAdmin || isAdmin || isSupport,
    },
    tables: {
      create: isSuperAdmin,
      edit: isSuperAdmin || isAdmin || isSupport,
      remove: isSuperAdmin || isAdmin || isSupport,
      sort: isSuperAdmin,
      enableDisable: isSuperAdmin || isAdmin || isSupport,
    },
    banners: {
      createStart: isSuperAdmin || isSupport,
      create: isSuperAdmin || isAdmin || isSupport,
      edit: isSuperAdmin || isAdmin || isSupport,
      remove: isSuperAdmin || isAdmin || isSupport,
      sort: isSuperAdmin || isAdmin || isSupport,
    },
    configs: {
      withdrawalTax: isSuperAdmin || isAdmin || isSupport,
      minWithdrawal: isSuperAdmin || isAdmin || isSupport,
      dailyWithdrawal: isSuperAdmin || isAdmin || isSupport,
      unico: isSuperAdmin,
      goals: isSuperAdmin || isAdmin || isSupport,
    },
    raffles: {
      createStart: isSuperAdmin || isSupport,
      create: isSuperAdmin || isAdmin || isSupport,
      edit: isSuperAdmin || isAdmin || isSupport,
      execute: isSuperAdmin || isAdmin || isSupport,
    },
  };

  return (
    <AuthContext.Provider
      value={{
        signedIn,
        user: data,
        signin,
        signout,
        isSuperAdmin,
        isAdmin,
        isManager,
        isSupervisor,
        isLoading,
        isLeader,
        isSeller,
        isSupport,
        PERMISSIONS,
      }}
    >
      {signedIn && !accessReady && !isError && !isLoadingExemptRoute ? (
        <AccessLoadingScreen
          isLoading={isLoading}
          onComplete={() => setAccessReady(true)}
        />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
